import React, { useState, useEffect } from 'react';
import { X, Save } from 'lucide-react';
import BASE_URL from '../services/api';
import { MANUAL_SECTIONS } from '../data/manualFields';

/**
 * ManualEntryModal
 *  props:
 *    sectionKey — key from MANUAL_SECTIONS
 *    district   — 'STATE' or district name
 *    initialValues — { [field_key]: value } (from server)
 *    onClose()
 *    onSaved()   — callback after successful save
 */
const ManualEntryModal = ({ sectionKey, district = 'STATE', initialValues = {}, onClose, onSaved }) => {
  const meta = MANUAL_SECTIONS[sectionKey];
  const [values, setValues] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    setValues(initialValues || {});
  }, [initialValues, sectionKey]);

  if (!meta) return null;

  const handleChange = (key, v) => {
    setValues(prev => ({ ...prev, [key]: v }));
  };

  const handleSave = async () => {
    setSaving(true); setError('');
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const entries = meta.fields.map(f => ({
        section_key: sectionKey,
        field_key: f.key,
        period: meta.period || '',
        value: values[f.key] ?? null,
        value_type: f.type,
      }));
      const res = await fetch(`${BASE_URL}/dashboard/manual`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': 'A9x2mK_Lp5QvR8tN1wZ4jY7sB0uC3fE6hG9iI2kM5nO8pQ1rS4tU7vX0zW3yZ6aB',
        },
        body: JSON.stringify({
          district,
          updated_by: user.email || user.username || 'admin',
          entries,
        }),
      });
      const json = await res.json();
      if (!res.ok || !json.success) {
        setError(json.message || `HTTP ${res.status}`);
        return;
      }
      onSaved && onSaved();
      onClose && onClose();
    } catch (err) {
      setError(err.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const renderInput = (f) => {
    const v = values[f.key] ?? '';
    const common = 'w-full px-3 py-2 border border-gray-300 focus:outline-none focus:border-[#3d82c4] text-sm bg-white';
    if (f.type === 'number') {
      return <input type="number" step="any" className={common} value={v} onChange={(e) => handleChange(f.key, e.target.value)} />;
    }
    if (f.type === 'date') {
      return <input type="date" className={common} value={v} onChange={(e) => handleChange(f.key, e.target.value)} />;
    }
    if (f.type === 'boolean') {
      return (
        <select className={common} value={v} onChange={(e) => handleChange(f.key, e.target.value)}>
          <option value="">—</option>
          <option value="Yes">Yes</option>
          <option value="No">No</option>
        </select>
      );
    }
    return <input type="text" className={common} value={v} onChange={(e) => handleChange(f.key, e.target.value)} />;
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
      <div className="bg-white w-full max-w-4xl max-h-[90vh] flex flex-col">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold text-gray-800">{meta.title}</h2>
            <p className="text-xs text-gray-500 mt-0.5">
              District: <strong>{district === 'STATE' ? 'All Tamil Nadu' : district}</strong> · Period: <strong>{meta.period || '—'}</strong>
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700"><X className="w-5 h-5" /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-2 text-sm mb-4">{error}</div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {meta.fields.map(f => (
              <div key={f.key}>
                <label className="text-xs font-semibold text-gray-600 mb-1 block">{f.label}</label>
                {renderInput(f)}
              </div>
            ))}
          </div>
        </div>

        <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 bg-gray-100 text-gray-700 text-sm font-medium hover:bg-gray-200">Cancel</button>
          <button onClick={handleSave} disabled={saving}
            className="px-4 py-2 bg-[#3d82c4] text-white text-sm font-medium hover:bg-[#2d6fad] disabled:opacity-60 flex items-center gap-2">
            <Save className="w-4 h-4" />
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ManualEntryModal;
